import {
  awaitSiteWideUnlockReadback, confirmSiteWideUnlockReadback, reportSiteWideUnlockSaveError,
  SiteWideUnlockForm, siteWideUnlockPayload, siteWideUnlockAccountDestinationError, siteWideUnlockServerError
} from '../ipop/site-wide-unlock/site-wide-unlock-settings';
import {Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Title} from '@angular/platform-browser';
import {ActivatedRoute, ParamMap, Params, Router} from '@angular/router';
import {UntypedFormGroup} from '@angular/forms';
import {MenuItem, MessageService} from 'primeng/api';
import {combineLatest, repeat, switchMap, Subject, takeUntil, tap} from 'rxjs';
import {OfferRegistryProcessStatusInterface} from '../../../../model/offer-registry-process-status.interface';
import {AccountInterface} from '../../../../model/account.interface';
import {AccountService} from '../../../../service/account.service';
import {IpopConfiguration, IpopConfigurationUpdate} from '../../../../model/ipop-configuration.model';
import {NavigationService} from '../../../../service/navigation.service';
import {IpopConfigurationService} from '../../../../service/ipop-configuration.service';
import {OfferRegistryProcessorSyncService} from '../../../../service/offer-registry-processor-sync.service';
import {ipopConfigurationRoutePaths} from '../../../../ipop-configuration-route.service';
import {
  IpopRuleWorkflowRulesListComponent
} from './rules-list/ipop-rule-workflow-rules-list.component';
import {
  OfferRuleWorkflowAcceptance,
  OfferRuleWorkflowBulkRequest,
  OfferRuleWorkflowCommandStatusResponse,
  OfferRuleWorkflowOperations,
  OfferRuleWorkflowPhase
} from './models/offer-rule-workflow-api.model';
import {OfferRuleWorkflowService} from './services/offer-rule-workflow.service';

const accountIdPattern = /^[1-9]\d*$/;
const accountRouteRestoreErrorDetail = 'Unable to keep the current account open. Please try again.';
const duplicateQueueStatusCode = 409;
const menuConfigurationFile = 'settings-view-side-menu.configuration.json';
const maximumBulkOperations = 50;
const offerSetRulesTab = 'offerSetRules';
const pageTitle = 'IPOP';
const privateOffersTab = 'privateOffers';
const processQueueErrorDetail = 'Unable to queue rule processing';
const processStatusLoadErrorDetail = 'Unable to retrieve offer registry process status';
const processStatusPollingIntervalMilliseconds = 3000;
const rulesProcessingToastDetail = 'The rules are being processed, offer data will be processed and synced within the next 10 minutes.';
const ruleSaveNavigationBlockedSummary = 'Save in progress';
const tabQueryParameter = 'tab';
const unsavedRuleNavigationConfirmationMessage = 'You have unsaved rule changes. Leave this page and discard them?';
const vdpOffersTab = 'vdpOffers';
const supportedTabs = [privateOffersTab, vdpOffersTab, offerSetRulesTab, 'siteWideUnlock'];
const commandRecoveryStorageKey = 'ipopOfferRuleWorkflowCommand';
const commandPollingDelaysMilliseconds = [1000, 2000, 3000, 5000, 8000];
const commandMaximumPollingDelayMilliseconds = 8000;
const commandStatusDismissalDelayMilliseconds = 10000;
const maximumCommandPollTransportRetries = 3;
const workflowSchemaVersion = 1 as const;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const commandPhaseRanks: Readonly<Record<OfferRuleWorkflowPhase, number>> = {
  queued: 0,
  persisting: 1,
  syncing_disclosures: 2,
  processing: 3,
  completed: 4,
  failed: 4
};
const commandPhaseMessages: Readonly<Record<OfferRuleWorkflowPhase, string>> = {
  queued: 'Rules are queued for saving.',
  persisting: 'Rules are being saved.',
  syncing_disclosures: 'Rules were saved. Disclosures are being synchronized.',
  processing: 'Rules were saved. Offer registry processing is running.',
  completed: 'Rules were saved and processed successfully.',
  failed: 'The rule workflow ended with a controlled failure.'
};

interface AcceptedCommandCorrelation {
  readonly accountId: number;
  readonly requestId: string;
  readonly commandId: number;
  readonly statusUri: string;
}

@Component({
  selector: 'auto-ipop-rule-workflow-enhancement-configuration',
  templateUrl: './ipop-rule-workflow-enhancement-configuration.component.html',
  styleUrls: ['./ipop-rule-workflow-enhancement-configuration.component.scss'],
  standalone: false
})
export class IpopRuleWorkflowEnhancementConfigurationComponent implements OnInit, OnDestroy
{
  @ViewChild(IpopRuleWorkflowRulesListComponent)
  private rulesListComponent?: IpopRuleWorkflowRulesListComponent;

  account: AccountInterface | null = null;
  accountId = 0;
  activeTab = privateOffersTab;
  settingsSideNav: MenuItem[] = [];
  privateOffersForm: UntypedFormGroup | null = null;
  vdpOffersForm: UntypedFormGroup | null = null;
  siteWideUnlockForm: SiteWideUnlockForm | null = null;
  ipopConfiguration: IpopConfiguration | null = null;
  isSaving = false;
  isSavingRules = false;
  isProcessingRules = false;
  isProcessStatusInitialized = false;
  isOfferRegistryProcessingActive = false;
  processStatus: OfferRegistryProcessStatusInterface | null = null;
  commandPhaseMessage = '';
  commandOutcomeMessage = '';
  canRetryRuleSaveAcceptance = false;

  private acceptedQueryParameters: Params = {};
  private activeProcessRequestId: number | null = null;
  private activeRuleSaveAttemptId: number | null = null;
  private hasUnsavedRuleChangesState = false;
  private isComponentDestroyed = false;
  private isProcessStatusLoadFailed = false;
  private processRequestSequence = 0;
  private processStatusLoadSequence = 0;
  private processStatusPollingSequence = 0;
  private ruleSaveAttemptSequence = 0;
  private acceptedCommand: AcceptedCommandCorrelation | null = null;
  private pendingAcceptanceRequest: OfferRuleWorkflowBulkRequest | null = null;
  private commandPollingGeneration = 0;
  private commandPollingTimer: number | null = null;
  private commandStatusDismissalTimer: number | null = null;
  private commandPollingDelayIndex = 0;
  private commandPollTransportFailures = 0;
  private lastCommandPhaseRank = -1;
  private processStatusPollingSubject = new Subject<void>();
  private settingsRequestCancellationSubject = new Subject<void>();
  private destroySubject = new Subject<void>();

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    private router: Router,
    private navigationService: NavigationService,
    private messageService: MessageService,
    private ipopConfigurationService: IpopConfigurationService,
    private accountService: AccountService,
    private offerRegistryProcessorSyncService: OfferRegistryProcessorSyncService,
    private offerRuleWorkflowService: OfferRuleWorkflowService
  )
  {
  }

  /** Initializes page state from the authenticated route. */
  ngOnInit(): void
  {
    this.titleService.setTitle(pageTitle);
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(takeUntil(this.destroySubject))
      .subscribe(([params, queryParams]) => {
        this.handleRouteState(params, queryParams);
      });
  }

  /** Releases route subscriptions. */
  ngOnDestroy(): void
  {
    this.isComponentDestroyed = true;
    this.cancelCommandPolling();
    this.cancelCommandStatusDismissal();
    this.invalidateRuleSaveAttempt();
    this.invalidateProcessRequest();
    this.cancelProcessStatusPolling();
    this.settingsRequestCancellationSubject.next();
    this.ipopConfiguration = null;
    this.siteWideUnlockForm = null;
    this.settingsRequestCancellationSubject.complete();
    this.destroySubject.next();
    this.destroySubject.complete();
    this.processStatusPollingSubject.complete();
  }

  /** Reports whether account-level rule processing can be requested. */
  getProcessRulesButtonDisabled(): boolean
  {
    const isDisabled = this.getRuleActionsDisabled() || this.hasUnsavedRuleChanges();

    return isDisabled;
  }

  /** Captures persisted Sitewide Unlock toggles; positions remain in the child. */
  private applySettingsResponse(settings: IpopConfiguration): void
  {
    this.ipopConfiguration = settings;
    if (this.activeTab === 'siteWideUnlock' && !settings.canConfigureSiteWideUnlock) {
      this.activeTab = 'privateOffers';
      this.siteWideUnlockForm = null;
    }
  }

  /** Validates active destinations from persisted data even when the Sitewide tab has not mounted. */
  getSiteWideUnlockError(): string | null
  {
    const message = siteWideUnlockAccountDestinationError(
      this.account, this.accountId, this.ipopConfiguration, this.siteWideUnlockForm
    );

    return message;
  }

  onSiteWideUnlockFormGroupInitialized(form: SiteWideUnlockForm): void
  {
    this.siteWideUnlockForm = form;
  }

  hasUnsavedSiteWideUnlockSettings(): boolean {
    return this.siteWideUnlockForm?.dirty === true;
  }

  /** Captures the shared Private Offers form for settings persistence. */
  onPrivateOffersFormGroupInitialized(form: UntypedFormGroup): void
  {
    this.privateOffersForm = form;
  }

  /** Captures the shared VDP Offers form for settings persistence. */
  onVdpOffersFormGroupInitialized(form: UntypedFormGroup): void
  {
    this.vdpOffersForm = form;
  }

  /** Reports whether the shared settings forms contain valid changes. */
  getSaveButtonDisabled(): boolean
  {
    let isDisabled = !(this.siteWideUnlockForm?.dirty && this.siteWideUnlockForm.valid);
    if (this.privateOffersForm?.dirty === true && this.privateOffersForm.valid) {
      isDisabled = false;
    }
    if (this.vdpOffersForm?.dirty === true && this.vdpOffersForm.valid) {
      isDisabled = false;
    }
    if (this.isOfferRegistryProcessingActive || this.isProcessingRules || this.isProcessStatusLoadFailed) {
      isDisabled = true;
    }

    if (!this.ipopConfiguration || this.ipopConfiguration.accountId !== this.accountId) {
      isDisabled = true;
    }
    if (this.isSaving || (this.siteWideUnlockForm?.dirty && !this.siteWideUnlockForm.valid)
      || this.getSiteWideUnlockError() !== null) {
      isDisabled = true;
    }

    return isDisabled;
  }

  /** Persists the shared Private Offers and VDP Offers settings. */
  saveSettings(): void
  {
    let payload: IpopConfigurationUpdate | null = null;
    if (this.ipopConfiguration !== null && !this.getSaveButtonDisabled()) {
      this.isSaving = true;
      payload = this.buildSaveSettingsPayload(this.ipopConfiguration);
    }
    if (payload !== null) {
      this.ipopConfigurationService.updateSettings(payload)
        .pipe(
          tap(() => awaitSiteWideUnlockReadback(this.siteWideUnlockForm)),
          switchMap(() => this.ipopConfigurationService.getSettings(payload.accountId)),
          tap(settings => confirmSiteWideUnlockReadback(this.siteWideUnlockForm, settings)),
          takeUntil(this.settingsRequestCancellationSubject),
          takeUntil(this.destroySubject)
        )
        .subscribe({
          next: (settings) => {
            this.applySettingsResponse(settings);
            this.handleSettingsSaveSuccess();
          },
          error: (error: unknown) => {
            reportSiteWideUnlockSaveError(this.siteWideUnlockForm, error);
            this.handleSettingsSaveError(error);
          },
          complete: () => {
            this.isSaving = false;
          }
        });
    }
  }

  /** Reports whether external page state locks rule mutations. */
  getRuleActionsDisabled(): boolean
  {
    const statusUnavailable = !this.isProcessStatusInitialized || this.isProcessStatusLoadFailed;
    const operationInProgress = this.isOfferRegistryProcessingActive
      || this.isProcessingRules
      || this.hasUnresolvedRuleSave()
      || this.activeProcessRequestId !== null;
    const isDisabled = statusUnavailable || operationInProgress || !this.isValidAccountId(this.accountId);

    return isDisabled;
  }

  /** Queues processing for all rules belonging to the current account. */
  processRules(): void
  {
    if (!this.getProcessRulesButtonDisabled()) {
      this.queueProcessRulesForCurrentAccount();
    }
  }

  /** Reports reversible local Offer Rule changes for navigation protection. */
  hasUnsavedRuleChanges(): boolean
  {
    const hasUnsavedChanges = this.hasUnsavedRuleChangesState === true;

    return hasUnsavedChanges;
  }

  /** Reports a rule save that may still be active in the backend. */
  hasUnresolvedRuleSave(): boolean
  {
    const hasUnresolvedSave = this.pendingAcceptanceRequest !== null
      || this.activeRuleSaveAttemptId !== null
      || this.acceptedCommand !== null
      || this.isSavingRules;

    return hasUnresolvedSave;
  }

  /** Explains why navigation is unavailable during a rule save. */
  showRuleSaveNavigationBlockedMessage(): void
  {
    const detail = `Rules are currently being saved for account ${this.accountId}. `
      + 'Wait for the save to finish before changing accounts.';
    this.messageService.add({
      severity: 'info',
      summary: ruleSaveNavigationBlockedSummary,
      detail
    });
  }

  /** Tracks the list-derived dirty state. */
  handleUnsavedRuleChangesChange(hasUnsavedRuleChanges: boolean): void
  {
    this.hasUnsavedRuleChangesState = hasUnsavedRuleChanges;
  }

  /** Starts one guarded asynchronous aggregate workflow command. */
  saveRules(operations: OfferRuleWorkflowOperations): void
  {
    if (this.canStartWorkflowSave(operations)) {
      const request = this.buildWorkflowRequest(operations);
      this.pendingAcceptanceRequest = request;
      this.submitWorkflowAcceptance(request);
    }
  }

  /** Repeats only the unaccepted transport request with its original request ID and payload. */
  retryRuleSaveAcceptance(): void
  {
    const request = this.pendingAcceptanceRequest;
    if (request !== null && this.canRetryRuleSaveAcceptance && !this.isSavingRules) {
      this.submitWorkflowAcceptance(request);
    }
  }

  /** Activates the browser warning for unsaved or unresolved rules. */
  @HostListener('window:beforeunload', ['$event'])
  confirmOnBrowserClose(event: BeforeUnloadEvent): void
  {
    if (this.hasUnsavedRuleChanges() || this.hasUnresolvedRuleSave() || this.hasUnsavedSiteWideUnlockSettings()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  /** Applies route account and tab state after protecting local changes. */
  private handleRouteState(params: ParamMap, queryParams: ParamMap): void
  {
    const routeAccountId = this.parseRouteAccountId(params.get('accountId'));
    const accountChanged = routeAccountId !== this.accountId;
    const shouldApplyRoute = this.confirmReusedRouteAccountChange(routeAccountId, accountChanged);
    if (shouldApplyRoute) {
      if (accountChanged) {
        this.prepareForAccountChange();
      }
      this.accountId = routeAccountId ?? 0;
      this.acceptedQueryParameters = this.copyQueryParameters(queryParams);
      this.applyRequestedTab(queryParams.get(tabQueryParameter));
      this.initializeRouteAccount(accountChanged);
    }
  }

  /** Protects a reused-route account replacement while rules are unresolved. */
  private confirmReusedRouteAccountChange(routeAccountId: number | null, accountChanged: boolean): boolean
  {
    let shouldApplyRoute = true;
    const hasCurrentAccount = this.isValidAccountId(this.accountId);
    if (accountChanged && hasCurrentAccount && this.hasUnresolvedRuleSave()) {
      shouldApplyRoute = false;
      this.showRuleSaveNavigationBlockedMessage();
      this.restoreAcceptedAccountRoute();
    }
    if (accountChanged && hasCurrentAccount && shouldApplyRoute && (this.hasUnsavedRuleChanges() || this.hasUnsavedSiteWideUnlockSettings())) {
      let confirmationMessage = unsavedRuleNavigationConfirmationMessage;
      if (this.hasUnsavedSiteWideUnlockSettings()) {
        confirmationMessage = 'You have unsaved settings. Leave this account and discard them?';
      }
      shouldApplyRoute = window.confirm(confirmationMessage);
      if (!shouldApplyRoute) {
        this.restoreAcceptedAccountRoute();
      }
    }

    return shouldApplyRoute;
  }

  /** Creates a detached copy of accepted query parameters. */
  private copyQueryParameters(queryParams: ParamMap): Params
  {
    const copiedParameters: Params = {};
    for (const key of queryParams.keys) {
      const values = queryParams.getAll(key);
      let copiedValue: string | string[] = values;
      if (values.length === 1) {
        copiedValue = values[0] ?? '';
      }
      copiedParameters[key] = copiedValue;
    }

    return copiedParameters;
  }

  /** Restores the last accepted account and query state after cancellation. */
  private restoreAcceptedAccountRoute(): void
  {
    const routePath = ipopConfigurationRoutePaths.enhanced;
    this.router.navigate(
      [routePath, this.accountId],
      {queryParams: this.acceptedQueryParameters, replaceUrl: true}
    ).then((wasRestored) => {
      if (!wasRestored) {
        this.showAccountRouteRestoreError();
      }
    }, () => {
      this.showAccountRouteRestoreError();
    });
  }

  /** Shows a controlled navigation restoration failure. */
  private showAccountRouteRestoreError(): void
  {
    this.messageService.add({
      severity: 'error',
      summary: 'Navigation error',
      detail: accountRouteRestoreErrorDetail
    });
  }

  /** Parses a strict positive account identifier. */
  private parseRouteAccountId(value: string | null): number | null
  {
    let accountId: number | null = null;

    if (value !== null && accountIdPattern.test(value)) {
      const parsedAccountId = Number(value);
      if (Number.isSafeInteger(parsedAccountId)) {
        accountId = parsedAccountId;
      }
    }

    return accountId;
  }

  /** Selects an allow-listed IPOP tab. */
  private applyRequestedTab(requestedTab: string | null): void
  {
    this.activeTab = privateOffersTab;
    if (requestedTab !== null && supportedTabs.includes(requestedTab)) {
      this.activeTab = requestedTab;
    }
    if (this.activeTab === 'siteWideUnlock' && this.ipopConfiguration !== null && !this.ipopConfiguration.canConfigureSiteWideUnlock) {
      this.activeTab = privateOffersTab;
    }
  }

  /** Initializes account-scoped navigation and process status once per account. */
  private initializeRouteAccount(accountChanged: boolean): void
  {
    if (accountChanged && this.isValidAccountId(this.accountId)) {
      this.initializeNavigation(this.accountId);
      this.loadSettings(this.accountId);
      this.loadProcessStatus(this.accountId);
      this.recoverAcceptedCommand(this.accountId);
    }
    if (accountChanged && !this.isValidAccountId(this.accountId)) {
      this.handleInvalidAccountId();
    }
  }

  /** Loads account-scoped settings navigation. */
  private initializeNavigation(accountId: number): void
  {
    this.navigationService
      .getMenuItems(accountId, menuConfigurationFile)
      .then((navItems) => {
        if (!this.isComponentDestroyed && this.accountId === accountId) {
          this.settingsSideNav = navItems;
        }
      }, () => {
        if (!this.isComponentDestroyed && this.accountId === accountId) {
          this.settingsSideNav = [];
        }
      });
  }

  /** Loads the configuration consumed by the shared settings tabs. */
  private loadSettings(accountId: number): void
  {
    this.settingsRequestCancellationSubject.next();
    this.ipopConfiguration = null;
    this.siteWideUnlockForm = null;
    this.loadAccountWebsite(accountId);
    this.ipopConfigurationService.getSettings(accountId)
      .pipe(
        takeUntil(this.settingsRequestCancellationSubject),
        takeUntil(this.destroySubject)
      )
      .subscribe({
        next: (settings) => {
          if (!this.isComponentDestroyed && this.accountId === accountId) {
            this.applySettingsResponse(settings);
          }
        },
        error: () => {
          if (!this.isComponentDestroyed && this.accountId === accountId) {
            this.showSettingsLoadError();
          }
        }
      });
  }

  /** Keeps destination validation tied to the current account during route changes. */
  private loadAccountWebsite(accountId: number): void
  {
    this.account = null;
    this.accountService.getAccount(accountId)
      .pipe(takeUntil(this.settingsRequestCancellationSubject), takeUntil(this.destroySubject))
      .subscribe({
        next: (account) => {
          if (!this.isComponentDestroyed && this.accountId === accountId) {
            this.account = account;
          }
        },
        error: () => {
          if (!this.isComponentDestroyed && this.accountId === accountId) {
            this.showSettingsLoadError();
          }
        }
      });
  }

  /** Builds the established full IPOP settings update contract. */
  private buildSaveSettingsPayload(ipopConfiguration: IpopConfiguration): IpopConfigurationUpdate
  {
    const privateForm = this.privateOffersForm;
    const vdpForm = this.vdpOffersForm;
    let privateOfferDisclosure = '';
    const disclosureValue = privateForm?.get('privateOfferDisclosure')?.value;
    if (typeof disclosureValue === 'string') {
      privateOfferDisclosure = disclosureValue.trim();
    }
    const payload: IpopConfigurationUpdate = {
      ...siteWideUnlockPayload(this.siteWideUnlockForm),
      id: ipopConfiguration.id,
      accountId: this.accountId,
      includePrivateOffersInExternalAdvertising: this.resolveBooleanControlValue(privateForm, 'includePrivateOffersInExternalAdvertising', false),
      unlockPrivateOffersEnabled: this.resolveBooleanControlValue(privateForm, 'unlockPrivateOffersEnabled', false),
      vdpOfferWidgetEnabled: this.resolveBooleanControlValue(vdpForm, 'vdpOfferWidgetEnabled', false),
      displaySavingsMessage: this.resolveBooleanControlValue(privateForm, 'displaySavingsMessage', true),
      savingsMessage: this.resolveStringControlValue(privateForm, 'savingsMessage', 'Instant Savings Unlocked'),
      savingsMessageBackgroundColor: this.resolveStringControlValue(privateForm, 'savingsMessageBackgroundColor', '#DDE9E2'),
      offerTextColor: this.resolveStringControlValue(vdpForm, 'offerTextColor', '#000000'),
      privateOfferTextColor: this.resolveStringControlValue(privateForm, 'privateOfferTextColor', '#000000'),
      privateOfferButtonColor: this.resolveStringControlValue(privateForm, 'privateOfferButtonColor', '#16A34A'),
      showPriceComparison: this.resolveBooleanControlValue(privateForm, 'showPriceComparison', false),
      displayUnlockedSellingPrice: this.resolveBooleanControlValue(privateForm, 'displayUnlockedSellingPrice', false),
      preUnlockCtaText: this.resolveStringControlValue(privateForm, 'preUnlockCtaText', 'Unlock Your Special Offer'),
      postUnlockCtaText: this.resolveStringControlValue(privateForm, 'postUnlockCtaText', 'Claim Your Special Offer'),
      ctaTextFontSize: this.resolveNumericControlValue(vdpForm, 'ctaTextFontSize', 22),
      ctaButtonColor: this.resolveStringControlValue(vdpForm, 'ctaButtonColor', '#000000'),
      privateOfferDisclosure: privateOfferDisclosure,
      clickToCallEnabled: this.resolveBooleanControlValue(vdpForm, 'clickToCallEnabled', true),
      clickToCallText: this.resolveStringControlValue(vdpForm, 'clickToCallText', '[DealerPhone]'),
      clickToCallTextFontSize: this.resolveNumericControlValue(vdpForm, 'clickToCallTextFontSize', 22),
      clickToCallBackgroundColor: this.resolveStringControlValue(vdpForm, 'clickToCallBackgroundColor', '#000000'),
      clickToCallTextColor: this.resolveStringControlValue(vdpForm, 'clickToCallTextColor', '#ffffff'),
      showCtaWithoutOffers: this.resolveBooleanControlValue(vdpForm, 'showCtaWithoutOffers', false)
    };

    return payload;
  }

  /** Reads an exact boolean control value with a safe default. */
  private resolveBooleanControlValue(form: UntypedFormGroup | null, controlName: string, defaultValue: boolean): boolean
  {
    let result = defaultValue;
    const value = form?.get(controlName)?.value;
    if (value === true) {
      result = true;
    }
    if (value === false) {
      result = false;
    }

    return result;
  }

  /** Reads a trimmed non-empty string control value with a safe default. */
  private resolveStringControlValue(form: UntypedFormGroup | null, controlName: string, defaultValue: string): string
  {
    let result = defaultValue;
    const value = form?.get(controlName)?.value;
    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (trimmedValue !== '') {
        result = trimmedValue;
      }
    }

    return result;
  }

  /** Reads a numeric control value with a safe default. */
  private resolveNumericControlValue(form: UntypedFormGroup | null, controlName: string, defaultValue: number): number
  {
    let result = defaultValue;
    const value = form?.get(controlName)?.value;
    if (typeof value === 'number') {
      result = value;
    }

    return result;
  }

  /** Completes a successful settings save and resets form dirtiness. */
  private handleSettingsSaveSuccess(): void
  {
    this.messageService.add({severity: 'success', summary: 'Saved', detail: 'Settings updated'});
    this.siteWideUnlockForm?.markAsPristine();
    this.privateOffersForm?.markAsPristine();
    this.vdpOffersForm?.markAsPristine();
  }

  /** Reports a controlled settings-save failure. */
  private handleSettingsSaveError(error: unknown): void
  {
    const destinationMessage = siteWideUnlockServerError(error);
    this.isSaving = false;
    this.messageService.add({severity: 'error', summary: 'Error', detail: destinationMessage ?? 'Unable to confirm saved settings. Your edits are retained; reload to verify before retrying.'});
  }

  /** Reports a controlled settings-load failure. */
  private showSettingsLoadError(): void
  {
    this.messageService.add({severity: 'error', summary: 'Error', detail: 'Failed to load settings'});
  }

  /** Verifies a complete aggregate operation set can begin one command. */
  private canStartWorkflowSave(operations: OfferRuleWorkflowOperations): boolean
  {
    const operationCount = operations.creates.length + operations.updates.length + operations.deletes.length;
    const canSave = operationCount > 0
      && operationCount <= maximumBulkOperations
      && this.hasUnsavedRuleChanges()
      && !this.getRuleActionsDisabled();

    return canSave;
  }

  /** Builds the frozen version-1 command envelope. */
  private buildWorkflowRequest(operations: OfferRuleWorkflowOperations): OfferRuleWorkflowBulkRequest
  {
    const request: OfferRuleWorkflowBulkRequest = {
      schemaVersion: workflowSchemaVersion,
      requestId: this.generateWorkflowRequestId(),
      accountId: this.accountId,
      creates: operations.creates,
      updates: operations.updates,
      deletes: operations.deletes
    };

    return request;
  }

  /** Generates a cryptographically strong version-4 request identifier. */
  private generateWorkflowRequestId(): string
  {
    const requestId = window.crypto.randomUUID();
    if (!uuidPattern.test(requestId)) {
      throw new Error('A secure workflow request identifier could not be generated.');
    }

    return requestId;
  }

  /** Submits one deliberate acceptance request without process preflight. */
  private submitWorkflowAcceptance(request: OfferRuleWorkflowBulkRequest): void
  {
    const saveAttemptId = this.startRuleSaveAttempt();
    this.canRetryRuleSaveAcceptance = false;
    this.commandOutcomeMessage = '';
    this.offerRuleWorkflowService.submitBulk(request)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (acceptance) => {
          this.handleWorkflowAcceptance(acceptance, request, saveAttemptId);
        },
        error: (error: unknown) => {
          this.handleWorkflowAcceptanceError(error, request, saveAttemptId);
        }
      });
  }

  /** Accepts only the response belonging to the pending account and request. */
  private handleWorkflowAcceptance(
    acceptance: OfferRuleWorkflowAcceptance,
    request: OfferRuleWorkflowBulkRequest,
    saveAttemptId: number
  ): void
  {
    if (this.isCurrentRuleSaveAttempt(request.accountId, saveAttemptId)) {
      const requestMatches = acceptance.requestId === request.requestId;
      if (requestMatches) {
        this.activateAcceptedCommand(acceptance, request);
      }
      if (!requestMatches) {
        this.rejectMalformedAcceptance(saveAttemptId);
      }
    }
  }

  /** Stores minimum command correlation and begins status observation. */
  private activateAcceptedCommand(
    acceptance: OfferRuleWorkflowAcceptance,
    request: OfferRuleWorkflowBulkRequest
  ): void
  {
    try {
      const statusUri = this.offerRuleWorkflowService.validateStatusUri(acceptance.statusUri, acceptance.commandId);
      const correlation = this.buildCommandCorrelation(acceptance, request.accountId, statusUri);
      this.acceptedCommand = correlation;
      this.pendingAcceptanceRequest = null;
      this.activeRuleSaveAttemptId = null;
      this.commandPhaseMessage = commandPhaseMessages.queued;
      this.rulesListComponent?.markRulesSaved();
      this.hasUnsavedRuleChangesState = false;
      this.persistAcceptedCommand(correlation);
      this.restartCommandPolling();
    } catch (error: unknown) {
      this.handleCommandStateError(error);
    }
  }

  /** Creates the allow-listed session correlation value. */
  private buildCommandCorrelation(
    acceptance: OfferRuleWorkflowAcceptance,
    accountId: number,
    statusUri: string
  ): AcceptedCommandCorrelation
  {
    const correlation: AcceptedCommandCorrelation = {
      accountId,
      requestId: acceptance.requestId,
      commandId: acceptance.commandId,
      statusUri
    };

    return correlation;
  }

  /** Keeps an unaccepted envelope only for an explicit transport retry. */
  private handleWorkflowAcceptanceError(
    error: unknown,
    request: OfferRuleWorkflowBulkRequest,
    saveAttemptId: number
  ): void
  {
    if (this.isCurrentRuleSaveAttempt(request.accountId, saveAttemptId)) {
      const transportFailure = error instanceof HttpErrorResponse && error.status === 0;
      this.canRetryRuleSaveAcceptance = transportFailure;
      if (!transportFailure) {
        this.pendingAcceptanceRequest = null;
      }
      this.commandOutcomeMessage = this.getAcceptanceErrorMessage(error);
      this.completeRuleSaveAttempt(saveAttemptId);
    }
  }

  /** Maps synchronous acceptance failures without exposing server internals. */
  private getAcceptanceErrorMessage(error: unknown): string
  {
    let message = 'The save request was not accepted. Review the staged rules and try again.';
    if (error instanceof HttpErrorResponse && error.status === 0) {
      message = 'The save response could not be reached. Retry the same request to avoid a duplicate command.';
    }
    if (error instanceof HttpErrorResponse && error.status === 404) {
      message = 'The workflow is unavailable for this account.';
    }
    if (error instanceof HttpErrorResponse && error.status === 413) {
      message = 'The save request exceeds the supported operation limit.';
    }

    return message;
  }

  /** Rejects an uncorrelated acceptance without inventing command identity. */
  private rejectMalformedAcceptance(saveAttemptId: number): void
  {
    this.pendingAcceptanceRequest = null;
    this.commandOutcomeMessage = 'The save acceptance response was invalid. No command will be observed.';
    this.completeRuleSaveAttempt(saveAttemptId);
  }

  /** Restarts polling with a new generation and reset transport budget. */
  private restartCommandPolling(): void
  {
    const correlation = this.acceptedCommand;
    if (correlation !== null) {
      this.cancelCommandPolling();
      this.commandPollTransportFailures = 0;
      this.commandPollingDelayIndex = 0;
      this.lastCommandPhaseRank = -1;
      this.pollCommandStatus(correlation, this.commandPollingGeneration);
    }
  }

  /** Loads only the accepted command status resource. */
  private pollCommandStatus(correlation: AcceptedCommandCorrelation, generation: number): void
  {
    this.offerRuleWorkflowService.loadCommandStatus(correlation.statusUri, correlation.commandId)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (response) => {
          this.handleCommandStatus(response, correlation, generation);
        },
        error: (error: unknown) => {
          this.handleCommandPollError(error, correlation, generation);
        }
      });
  }

  /** Applies only correlated, non-regressive command responses. */
  private handleCommandStatus(
    response: OfferRuleWorkflowCommandStatusResponse,
    correlation: AcceptedCommandCorrelation,
    generation: number
  ): void
  {
    if (this.isCurrentCommandObservation(correlation, generation)) {
      const correlated = this.isCommandResponseCorrelated(response, correlation);
      const progressive = commandPhaseRanks[response.phase] >= this.lastCommandPhaseRank;
      if (correlated && progressive && this.isStatusPhaseValid(response)) {
        this.applyCommandStatus(response, correlation, generation);
      }
      if (!correlated || !progressive || !this.isStatusPhaseValid(response)) {
        this.pauseMalformedCommandObservation(correlation, generation);
      }
    }
  }

  /** Checks response account, request, and command correlation. */
  private isCommandResponseCorrelated(
    response: OfferRuleWorkflowCommandStatusResponse,
    correlation: AcceptedCommandCorrelation
  ): boolean
  {
    const correlated = response.accountId === correlation.accountId
      && response.requestId === correlation.requestId
      && response.commandId === correlation.commandId;

    return correlated;
  }

  /** Enforces valid active and terminal status/phase pairings. */
  private isStatusPhaseValid(response: OfferRuleWorkflowCommandStatusResponse): boolean
  {
    const queued = response.status === 'queued' && response.phase === 'queued';
    const active = response.status === 'processing'
      && ['persisting', 'syncing_disclosures', 'processing'].includes(response.phase);
    const completed = response.status === 'completed' && response.phase === 'completed';
    const failed = response.status === 'failed' && response.phase === 'failed';
    const valid = queued || active || completed || failed;

    return valid;
  }

  /** Routes a verified status to active or terminal handling. */
  private applyCommandStatus(
    response: OfferRuleWorkflowCommandStatusResponse,
    correlation: AcceptedCommandCorrelation,
    generation: number
  ): void
  {
    this.commandPhaseMessage = commandPhaseMessages[response.phase];
    this.commandOutcomeMessage = '';
    this.commandPollTransportFailures = 0;
    this.advanceCommandPollingDelay(response.phase);
    if (response.status === 'queued' || response.status === 'processing') {
      this.scheduleCommandPoll(correlation, generation);
    }
    if (response.status === 'completed') {
      this.handleCompletedCommand(response);
    }
    if (response.status === 'failed') {
      this.handleFailedCommand(response);
    }
  }

  /** Advances bounded successful-status backoff, resetting on phase progress. */
  private advanceCommandPollingDelay(phase: OfferRuleWorkflowPhase): void
  {
    const phaseRank = commandPhaseRanks[phase];
    const phaseAdvanced = phaseRank > this.lastCommandPhaseRank;
    if (phaseAdvanced) {
      this.commandPollingDelayIndex = 0;
      this.lastCommandPhaseRank = phaseRank;
    }
    if (!phaseAdvanced) {
      const maximumIndex = commandPollingDelaysMilliseconds.length - 1;
      this.commandPollingDelayIndex = Math.min(this.commandPollingDelayIndex + 1, maximumIndex);
    }
  }

  /** Schedules one guarded command status request. */
  private scheduleCommandPoll(correlation: AcceptedCommandCorrelation, generation: number): void
  {
    const delay = commandPollingDelaysMilliseconds[this.commandPollingDelayIndex]
      ?? commandMaximumPollingDelayMilliseconds;
    this.commandPollingTimer = window.setTimeout(() => {
      this.commandPollingTimer = null;
      if (this.isCurrentCommandObservation(correlation, generation)) {
        this.pollCommandStatus(correlation, generation);
      }
    }, delay);
  }

  /** Retries status transport failures within a bounded policy. */
  private handleCommandPollError(
    error: unknown,
    correlation: AcceptedCommandCorrelation,
    generation: number
  ): void
  {
    if (this.isCurrentCommandObservation(correlation, generation)) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        this.invalidateUnavailableCommand();
      }
      if (!(error instanceof HttpErrorResponse) || error.status !== 404) {
        this.retryOrPauseCommandPolling(correlation, generation);
      }
    }
  }

  /** Automatically retries status transport failures without resubmitting the command. */
  private retryOrPauseCommandPolling(correlation: AcceptedCommandCorrelation, generation: number): void
  {
    this.commandPollTransportFailures += 1;
    if (this.commandPollTransportFailures > maximumCommandPollTransportRetries) {
      this.commandPollingDelayIndex = commandPollingDelaysMilliseconds.length - 1;
      this.commandOutcomeMessage = 'Command status is temporarily unavailable. Retrying automatically.';
    }
    this.scheduleCommandPoll(correlation, generation);
  }

  /** Continues observation after a stale, malformed, or regressive response. */
  private pauseMalformedCommandObservation(
    correlation: AcceptedCommandCorrelation,
    generation: number
  ): void
  {
    this.commandPollingDelayIndex = commandPollingDelaysMilliseconds.length - 1;
    this.commandOutcomeMessage = 'The command status response could not be safely applied. Retrying automatically.';
    this.scheduleCommandPoll(correlation, generation);
  }

  /** Applies or reloads a verified completed result before unlocking the rules list. */
  private handleCompletedCommand(response: OfferRuleWorkflowCommandStatusResponse): void
  {
    const reconciled = this.rulesListComponent?.reconcileCompletedStatus(response) === true;
    this.rulesListComponent?.markRulesSaved();
    this.hasUnsavedRuleChangesState = false;
    if (!reconciled) {
      this.rulesListComponent?.reloadAuthoritativeAggregates();
    }
    this.finishTerminalCommand();
    this.scheduleCommandStatusDismissal();
  }

  /** Preserves staged or committed state according to the verified checkpoint. */
  private handleFailedCommand(response: OfferRuleWorkflowCommandStatusResponse): void
  {
    const committed = response.checkpoint?.persistenceCommitted === true;
    if (committed) {
      this.rulesListComponent?.reconcileCommittedFailure(response);
      this.rulesListComponent?.markRulesSaved();
      this.hasUnsavedRuleChangesState = false;
      this.commandOutcomeMessage = this.getCommittedFailureMessage(response);
    }
    if (!committed) {
      this.handlePreCommitCommandFailure(response);
    }
    this.finishTerminalCommand();
  }

  /** Keeps correctable staged work and handles version conflict separately. */
  private handlePreCommitCommandFailure(response: OfferRuleWorkflowCommandStatusResponse): void
  {
    const error = response.error;
    if (error !== null && error.code === 'aggregate_version_conflict') {
      this.rulesListComponent?.handleVersionConflict(error);
      this.commandOutcomeMessage = 'Rules changed on the server. Your staged snapshot was preserved; restage explicitly.';
    }
    if (error !== null && error.code !== 'aggregate_version_conflict') {
      this.rulesListComponent?.applyPreCommitFailure(error);
      this.commandOutcomeMessage = 'The rules were not saved. Correct the controlled errors and save again.';
    }
    if (error === null) {
      this.commandOutcomeMessage = 'The rules were not saved. Review the staged changes and try again.';
    }
  }

  /** Presents post-commit operational guidance without retrying workflow phases. */
  private getCommittedFailureMessage(response: OfferRuleWorkflowCommandStatusResponse): string
  {
    let message = 'Rules were saved, but downstream work requires manual operational correction.';
    if (response.checkpoint?.failedPhase === 'syncing_disclosures') {
      message = 'Rules were saved, but disclosure synchronization requires manual operational correction.';
    }
    if (response.checkpoint?.failedPhase === 'processing') {
      message = 'Rules were saved, but registry processing requires manual operational correction.';
    }

    return message;
  }

  /** Ends a verified terminal command and clears only its recovery correlation. */
  private finishTerminalCommand(): void
  {
    this.cancelCommandPolling();
    this.clearAcceptedCommandStorage();
    this.acceptedCommand = null;
    this.pendingAcceptanceRequest = null;
    this.activeRuleSaveAttemptId = null;
    this.isSavingRules = false;
    this.canRetryRuleSaveAcceptance = false;
  }

  /** Cancels only browser observation, never the accepted backend command. */
  private cancelCommandPolling(): void
  {
    this.commandPollingGeneration += 1;
    if (this.commandPollingTimer !== null) {
      window.clearTimeout(this.commandPollingTimer);
      this.commandPollingTimer = null;
    }
  }

  /** Clears a successful terminal status after it has remained visible briefly. */
  private scheduleCommandStatusDismissal(): void
  {
    this.cancelCommandStatusDismissal();
    this.commandStatusDismissalTimer = window.setTimeout(() => {
      this.commandPhaseMessage = '';
      this.commandOutcomeMessage = '';
      this.commandStatusDismissalTimer = null;
    }, commandStatusDismissalDelayMilliseconds);
  }

  /** Prevents an earlier successful command from clearing newer status text. */
  private cancelCommandStatusDismissal(): void
  {
    if (this.commandStatusDismissalTimer !== null) {
      window.clearTimeout(this.commandStatusDismissalTimer);
      this.commandStatusDismissalTimer = null;
    }
  }

  /** Confirms one callback belongs to the active account and polling generation. */
  private isCurrentCommandObservation(
    correlation: AcceptedCommandCorrelation,
    generation: number
  ): boolean
  {
    const current = !this.isComponentDestroyed
      && this.accountId === correlation.accountId
      && this.acceptedCommand === correlation
      && this.commandPollingGeneration === generation;

    return current;
  }

  /** Handles a validated tenant-safe unavailable status resource. */
  private invalidateUnavailableCommand(): void
  {
    this.commandOutcomeMessage = 'The accepted command is unavailable for this account. It was not resubmitted.';
    this.finishTerminalCommand();
  }

  /** Persists only minimum session-scoped status correlation. */
  private persistAcceptedCommand(correlation: AcceptedCommandCorrelation): void
  {
    const serialized = JSON.stringify(correlation);
    window.sessionStorage.setItem(commandRecoveryStorageKey, serialized);
  }

  /** Restores status observation for the matching account without resubmission. */
  private recoverAcceptedCommand(accountId: number): void
  {
    try {
      const storedValue = window.sessionStorage.getItem(commandRecoveryStorageKey);
      const correlation = this.parseStoredCommandCorrelation(storedValue);
      if (correlation !== null && correlation.accountId === accountId) {
        this.acceptedCommand = correlation;
        this.commandPhaseMessage = 'Resuming accepted command status.';
        this.restartCommandPolling();
      }
    } catch (error: unknown) {
      this.handleCommandRecoveryError(error);
    }
  }

  /** Parses and validates untrusted browser storage correlation. */
  private parseStoredCommandCorrelation(storedValue: string | null): AcceptedCommandCorrelation | null
  {
    let correlation: AcceptedCommandCorrelation | null = null;
    if (storedValue !== null) {
      const parsed: unknown = JSON.parse(storedValue);
      if (!this.isStoredCommandCorrelation(parsed)) {
        throw new TypeError('Stored command correlation is invalid.');
      }
      const statusUri = this.offerRuleWorkflowService.validateStatusUri(parsed.statusUri, parsed.commandId);
      correlation = {...parsed, statusUri};
    }

    return correlation;
  }

  /** Validates exact minimum recovery fields and identifier formats. */
  private isStoredCommandCorrelation(value: unknown): value is AcceptedCommandCorrelation
  {
    let valid = false;
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      const record = value as Record<string, unknown>;
      const keys = Object.keys(record).sort().join(',');
      valid = keys === 'accountId,commandId,requestId,statusUri'
        && typeof record['accountId'] === 'number'
        && this.isValidAccountId(record['accountId'])
        && typeof record['commandId'] === 'number'
        && this.isValidAccountId(record['commandId'])
        && typeof record['requestId'] === 'string'
        && uuidPattern.test(record['requestId'])
        && typeof record['statusUri'] === 'string';
    }

    return valid;
  }

  /** Clears verified terminal or tenant-invalidated recovery state. */
  private clearAcceptedCommandStorage(): void
  {
    try {
      window.sessionStorage.removeItem(commandRecoveryStorageKey);
    } catch (error: unknown) {
      this.commandOutcomeMessage = this.getStorageErrorMessage(error);
    }
  }

  /** Invalidates malformed local recovery data without inferring command failure. */
  private handleCommandRecoveryError(error: unknown): void
  {
    try {
      window.sessionStorage.removeItem(commandRecoveryStorageKey);
    } catch (storageError: unknown) {
      this.commandOutcomeMessage = this.getStorageErrorMessage(storageError);
    }
    if (this.commandOutcomeMessage === '') {
      this.commandOutcomeMessage = this.getStorageErrorMessage(error);
    }
  }

  /** Produces a controlled local-storage message without exposing stored data. */
  private getStorageErrorMessage(error: unknown): string
  {
    let message = 'Command recovery is unavailable in this browser session.';
    if (!(error instanceof Error)) {
      message = 'Command recovery failed because of an unknown local browser error.';
    }

    return message;
  }

  /** Handles status URI or session-storage failures as controlled UI state. */
  private handleCommandStateError(error: unknown): void
  {
    this.activeRuleSaveAttemptId = null;
    this.commandOutcomeMessage = 'Command recovery state could not be stored or validated. The command was not resubmitted.';
    if (!(error instanceof Error)) {
      this.commandOutcomeMessage = 'Command recovery failed with an unknown local error. The command was not resubmitted.';
    }
    if (this.acceptedCommand !== null) {
      this.restartCommandPolling();
    }
    if (this.acceptedCommand === null) {
      this.cancelCommandPolling();
      this.pendingAcceptanceRequest = null;
      this.isSavingRules = false;
    }
  }

  /** Marks one uniquely identified save attempt active. */
  private startRuleSaveAttempt(): number
  {
    this.cancelCommandStatusDismissal();
    this.ruleSaveAttemptSequence += 1;
    this.activeRuleSaveAttemptId = this.ruleSaveAttemptSequence;
    this.isSavingRules = true;

    return this.ruleSaveAttemptSequence;
  }

  /** Verifies that an asynchronous result belongs to the active save. */
  private isCurrentRuleSaveAttempt(accountId: number, saveAttemptId: number): boolean
  {
    const isCurrent = !this.isComponentDestroyed
      && this.activeRuleSaveAttemptId === saveAttemptId
      && this.accountId === accountId;

    return isCurrent;
  }

  /** Clears a matching active save attempt. */
  private completeRuleSaveAttempt(saveAttemptId: number): void
  {
    if (this.activeRuleSaveAttemptId === saveAttemptId) {
      this.activeRuleSaveAttemptId = null;
      this.isSavingRules = false;
    }
  }

  /** Queues processing without serializing local rules or transient drafts. */
  private queueProcessRulesForCurrentAccount(): void
  {
    const accountId = this.accountId;
    const requestId = this.startProcessRequest();
    this.offerRegistryProcessorSyncService.queueProcessForAllAccountRules(accountId)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: () => {
          this.handleProcessRulesSuccess(accountId, requestId);
        },
        error: (error: unknown) => {
          this.handleProcessRulesError(error, accountId, requestId);
        }
      });
  }

  /** Starts a uniquely identified direct process request. */
  private startProcessRequest(): number
  {
    this.processRequestSequence += 1;
    this.activeProcessRequestId = this.processRequestSequence;
    this.isProcessingRules = true;

    return this.processRequestSequence;
  }

  /** Handles a current direct queue success. */
  private handleProcessRulesSuccess(accountId: number, requestId: number): void
  {
    if (this.isCurrentProcessRequest(accountId, requestId)) {
      this.showRulesProcessingToast();
      this.isOfferRegistryProcessingActive = true;
      this.loadProcessStatus(accountId);
      this.completeProcessRequest(accountId, requestId);
    }
  }

  /** Handles direct queue errors and duplicate requests. */
  private handleProcessRulesError(error: unknown, accountId: number, requestId: number): void
  {
    if (this.isCurrentProcessRequest(accountId, requestId)) {
      if (this.isDuplicateQueueResponse(error)) {
        this.refreshDuplicateProcessStatus(accountId, requestId);
      }
      if (!this.isDuplicateQueueResponse(error)) {
        this.showProcessQueueError();
        this.completeProcessRequest(accountId, requestId);
      }
    }
  }

  /** Confirms duplicate processing against current account status. */
  private refreshDuplicateProcessStatus(accountId: number, requestId: number): void
  {
    this.offerRegistryProcessorSyncService.getProcessStatus(accountId)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (status) => {
          this.handleDuplicateProcessStatus(status, accountId, requestId);
        },
        error: () => {
          if (this.isCurrentProcessRequest(accountId, requestId)) {
            this.handleProcessStatusError(accountId);
            this.completeProcessRequest(accountId, requestId);
          }
        }
      });
  }

  /** Treats duplicate queueing as active only when status confirms it. */
  private handleDuplicateProcessStatus(
    status: OfferRegistryProcessStatusInterface,
    accountId: number,
    requestId: number
  ): void
  {
    if (this.isCurrentProcessRequest(accountId, requestId)) {
      const processingConfirmed = status.accountId === accountId && status.incomplete === true;
      if (status.accountId === accountId) {
        this.handleProcessStatusSuccess(status, accountId);
      }
      if (processingConfirmed) {
        this.showRulesProcessingToast();
      }
      if (!processingConfirmed) {
        this.showProcessQueueError();
      }
      this.completeProcessRequest(accountId, requestId);
    }
  }

  /** Verifies that a direct queue callback belongs to the current account. */
  private isCurrentProcessRequest(accountId: number, requestId: number): boolean
  {
    const isCurrent = !this.isComponentDestroyed
      && this.activeProcessRequestId === requestId
      && this.accountId === accountId;

    return isCurrent;
  }

  /** Completes only the matching direct process request. */
  private completeProcessRequest(accountId: number, requestId: number): void
  {
    if (this.isCurrentProcessRequest(accountId, requestId)) {
      this.activeProcessRequestId = null;
      this.isProcessingRules = false;
    }
  }

  /** Loads account-level processing status with stale-response protection. */
  private loadProcessStatus(accountId: number): void
  {
    this.processStatusLoadSequence += 1;
    const loadSequence = this.processStatusLoadSequence;
    this.offerRegistryProcessorSyncService.getProcessStatus(accountId)
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (status) => {
          if (this.isCurrentStatusLoad(accountId, loadSequence)) {
            this.handleProcessStatusSuccess(status, accountId);
          }
        },
        error: () => {
          if (this.isCurrentStatusLoad(accountId, loadSequence)) {
            this.handleProcessStatusError(accountId);
          }
        }
      });
  }

  /** Verifies one current account status load. */
  private isCurrentStatusLoad(accountId: number, loadSequence: number): boolean
  {
    const isCurrent = !this.isComponentDestroyed
      && this.accountId === accountId
      && this.processStatusLoadSequence === loadSequence;

    return isCurrent;
  }

  /** Applies an expected account process status. */
  private handleProcessStatusSuccess(status: OfferRegistryProcessStatusInterface, accountId: number): void
  {
    if (status.accountId === accountId && this.accountId === accountId) {
      this.processStatus = status;
      this.isProcessStatusInitialized = true;
      this.isProcessStatusLoadFailed = false;
      this.isOfferRegistryProcessingActive = status.incomplete === true;
      if (this.isOfferRegistryProcessingActive) {
        this.startProcessStatusPolling(accountId);
      }
      if (!this.isOfferRegistryProcessingActive) {
        this.cancelProcessStatusPolling();
      }
    }
    if (status.accountId !== accountId && this.accountId === accountId) {
      this.handleProcessStatusError(accountId);
    }
  }

  /** Locks rule actions after a current status failure. */
  private handleProcessStatusError(accountId: number): void
  {
    if (this.accountId === accountId && !this.isComponentDestroyed) {
      this.isProcessStatusInitialized = false;
      this.isProcessStatusLoadFailed = true;
      this.isOfferRegistryProcessingActive = true;
      this.cancelProcessStatusPolling();
      this.showProcessStatusLoadError();
    }
  }

  /** Starts current-account status polling while processing is active. */
  private startProcessStatusPolling(accountId: number): void
  {
    this.cancelProcessStatusPolling();
    this.processStatusPollingSequence += 1;
    const pollingSequence = this.processStatusPollingSequence;
    this.offerRegistryProcessorSyncService.getProcessStatus(accountId)
      .pipe(
        repeat({delay: processStatusPollingIntervalMilliseconds}),
        takeUntil(this.processStatusPollingSubject),
        takeUntil(this.destroySubject)
      )
      .subscribe({
        next: (status) => {
          this.handleProcessPollingStatus(status, accountId, pollingSequence);
        },
        error: () => {
          if (this.isCurrentPollingRequest(accountId, pollingSequence)) {
            this.handleProcessStatusError(accountId);
          }
        }
      });
  }

  /** Applies only polling results for the current account and sequence. */
  private handleProcessPollingStatus(
    status: OfferRegistryProcessStatusInterface,
    accountId: number,
    pollingSequence: number
  ): void
  {
    if (this.isCurrentPollingRequest(accountId, pollingSequence)) {
      if (status.accountId === accountId) {
        this.processStatus = status;
        this.isProcessStatusInitialized = true;
        this.isProcessStatusLoadFailed = false;
        this.isOfferRegistryProcessingActive = status.incomplete === true;
        if (!this.isOfferRegistryProcessingActive) {
          this.cancelProcessStatusPolling();
        }
      }
      if (status.accountId !== accountId) {
        this.handleProcessStatusError(accountId);
      }
    }
  }

  /** Verifies a polling callback belongs to the active account sequence. */
  private isCurrentPollingRequest(accountId: number, pollingSequence: number): boolean
  {
    const isCurrent = !this.isComponentDestroyed
      && this.accountId === accountId
      && this.processStatusPollingSequence === pollingSequence;

    return isCurrent;
  }

  /** Stops current process status polling. */
  private cancelProcessStatusPolling(): void
  {
    this.processStatusPollingSequence += 1;
    this.processStatusPollingSubject.next();
  }

  /** Invalidates account-scoped requests before state replacement. */
  private prepareForAccountChange(): void
  {
    this.settingsRequestCancellationSubject.next();
    this.ipopConfiguration = null;
    this.siteWideUnlockForm = null;
    this.cancelCommandPolling();
    this.invalidateRuleSaveAttempt();
    this.invalidateProcessRequest();
    this.processStatusLoadSequence += 1;
    this.cancelProcessStatusPolling();
    this.processStatus = null;
    this.isProcessStatusInitialized = false;
    this.isProcessStatusLoadFailed = false;
    this.isOfferRegistryProcessingActive = true;
    this.hasUnsavedRuleChangesState = false;
    this.acceptedCommand = null;
    this.pendingAcceptanceRequest = null;
    this.commandPhaseMessage = '';
    this.commandOutcomeMessage = '';
    this.canRetryRuleSaveAcceptance = false;
    this.settingsSideNav = [];
    this.ipopConfiguration = null;
    this.privateOffersForm = null;
    this.vdpOffersForm = null;
    this.isSaving = false;
  }

  /** Invalidates pending save callbacks and unlocks local request state. */
  private invalidateRuleSaveAttempt(): void
  {
    this.ruleSaveAttemptSequence += 1;
    this.activeRuleSaveAttemptId = null;
    this.isSavingRules = false;
  }

  /** Invalidates pending direct processing callbacks. */
  private invalidateProcessRequest(): void
  {
    this.processRequestSequence += 1;
    this.activeProcessRequestId = null;
    this.isProcessingRules = false;
  }

  /** Handles an invalid route account without making service calls. */
  private handleInvalidAccountId(): void
  {
    this.isProcessStatusInitialized = false;
    this.isProcessStatusLoadFailed = true;
    this.isOfferRegistryProcessingActive = true;
    this.cancelProcessStatusPolling();
    this.showProcessStatusLoadError();
  }

  /** Checks whether an account identifier is a positive safe integer. */
  private isValidAccountId(accountId: number): boolean
  {
    const isValid = Number.isSafeInteger(accountId) && accountId > 0;

    return isValid;
  }

  /** Checks whether a service error is the current duplicate response. */
  private isDuplicateQueueResponse(error: unknown): boolean
  {
    let duplicateResponse = false;
    if (error instanceof HttpErrorResponse && error.status === duplicateQueueStatusCode) {
      duplicateResponse = true;
    }

    return duplicateResponse;
  }

  /** Shows the process queue success message. */
  private showRulesProcessingToast(): void
  {
    this.messageService.add({severity: 'success', summary: 'Success', detail: rulesProcessingToastDetail});
  }

  /** Shows a generic process queue failure. */
  private showProcessQueueError(): void
  {
    this.messageService.add({severity: 'error', summary: 'Error', detail: processQueueErrorDetail});
  }

  /** Shows a generic process status failure. */
  private showProcessStatusLoadError(): void
  {
    this.messageService.add({severity: 'error', summary: 'Error', detail: processStatusLoadErrorDetail});
  }
}
